import React from 'react';
import { Avatar } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AccountMenuPopup from './AccountMenuPopup';
import { selectCurrentSpace } from '@/store/selectors/spaceSelectors';
import { TeamRole } from '@/store/slices/spaceSlice';
import { useAccountMenuData } from './useAccountMenuData';
import { useBrand } from '@/contexts/BrandContext';
import AvatarIcon from '@/assets/images/avatar.png';
import type { AccountListItem } from '@/services/types';
import { AccountType } from '@/services/types';
import { useSpaceCredits } from '@/hooks/useSpaceCredits';

/**
 * 侧边栏底部账户区（V0.1 团队协作）—— 替代原 hover 单行账户卡。
 *
 * PRD 1.1：卡片两行（手机号 + 「当前：账户名」），点击展开浮层菜单（原 hover 改 click），
 * 浮层含摘要区/操作按钮/通用项，「切换账户」展开三组账户列表。
 * MainLayout 与 AgreementLayout 两份 Sidebar 共用本组件。
 */
const AccountMenu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentSpace = useSelector(selectCurrentSpace);
  const { hiddenUserMenuItems = [] } = useBrand();
  const { maskedMobile, accountList, loading, fetchAccountList, switchToAccount } =
    useAccountMenuData();
  // 米粒余额：浮层摘要区展示（useSpaceCredits 内含 account_list 主路 + /user/credits 兜底）
  const { credits, refresh: refreshCredits } = useSpaceCredits();
  const [open, setOpen] = React.useState(false);
  const [showSwitchList, setShowSwitchList] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // 点外部收起
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setShowSwitchList(false);
      }
    };
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setShowSwitchList(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [open]);

  /** 打开时拉数据（浮层每次打开重拉，保证米粒/团队列表新鲜） */
  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setShowSwitchList(false);
      fetchAccountList();
      refreshCredits();
    }
  };

  /** 点击账户项切换 */
  const handleSwitch = async (account: AccountListItem) => {
    const isCurrent = currentSpace
      ? account.user_id === currentSpace.teamId
      : account.account_type === AccountType.Personal;
    if (isCurrent) {
      setOpen(false);
      setShowSwitchList(false);
      return;
    }
    const ok = await switchToAccount(account);
    if (ok) {
      setOpen(false);
      setShowSwitchList(false);
    }
  };

  /** 当前空间显示名与角色后缀：个人=「个人账户」无角色；团队=团队名+「· 拥有者/成员」。
   * 名与角色拆开渲染（20261030 主公定稿）：名称超长省略，角色段固定展示不被挤掉 */
  const currentName = currentSpace ? currentSpace.teamName : t('space.personalAccount');
  const currentRole = currentSpace
    ? ` · ${currentSpace.role === TeamRole.Owner ? t('space.identityOwner') : t('space.identityMember')}`
    : '';

  return (
    <div
      ref={containerRef}
      style={{
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        height: '68px',
        boxSizing: 'border-box',
        background: '#101010',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {/* 账户卡片（两行） */}
      <div
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '197px',
          padding: '5px 12px',
          borderRadius: '8px',
          background: open ? '#1A1A1A' : 'transparent',
          boxSizing: 'border-box',
          transition: 'background 0.2s ease',
          cursor: 'pointer',
        }}
      >
        <Avatar
          size={28}
          src={undefined}
          style={{ backgroundColor: '#333', color: '#999', flexShrink: 0 }}
        >
          <img src={AvatarIcon} alt={t('common.defaultAvatar')} style={{ width: '100%', height: '100%' }} />
        </Avatar>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
          <span
            style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {maskedMobile || t('user.defaultName')}
          </span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              color: 'rgba(255,255,255,0.45)',
              fontSize: '11px',
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            {/* 「当前：团队名」整体可省略（前缀在段首，ellipsis 截尾部不会伤及）；角色段固定 */}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t('space.currentLabel')}：{currentName}
            </span>
            {currentRole && <span style={{ flexShrink: 0 }}>{currentRole}</span>}
          </span>
        </div>
      </div>

      {/* 用户菜单浮层（点击弹出，自账户区上方） */}
      {open && (
        <AccountMenuPopup
          maskedMobile={maskedMobile}
          credits={credits}
          accountList={accountList}
          loading={loading}
          showSwitchList={showSwitchList}
          hiddenUserMenuItems={hiddenUserMenuItems}
          onShowSwitchList={() => setShowSwitchList(v => !v)}
          onSwitchAccount={handleSwitch}
          onClose={() => {
            setOpen(false);
            setShowSwitchList(false);
          }}
          onNavigate={url => {
            setOpen(false);
            setShowSwitchList(false);
            navigate(url);
          }}
          onTeamManage={() => {
            setOpen(false);
            setShowSwitchList(false);
            navigate('/team');
          }}
        />
      )}
    </div>
  );
};

export default AccountMenu;
